import type { SidebarNavLinkProps } from '../components/SidebarNav/Link';

import React, { useContext, useMemo, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import type { ConfigContextValue, ProjectContextValue, UserContextValue } from '@core/context';
import { BaseUrlContext, ConfigContext, ProjectContext, UserContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useDashLinkUrl from '@core/hooks/useDashLinkUrl';
import { useProjectStore, useSuperHubStore } from '@core/store';

import useIsWebhookSetupComplete from '@routes/MyDevelopers/Setup/PersonalizedDocs/useIsWebhookSetupComplete';
import BasePanel from '@routes/SuperHub/components/BasePanel';
import { useSuperHubContext } from '@routes/SuperHub/components/Context';
import { SidebarNav, SidebarNavSection, SidebarNavLink } from '@routes/SuperHub/Settings/components';

import Dropdown from '@ui/Dropdown';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Menu from '@ui/Menu';
import MenuDivider from '@ui/Menu/Divider';
import MenuItem from '@ui/Menu/Item';
import Truncate from '@ui/Truncate';

import CustomComponents from '../forms/CustomComponents';
import GitConnection from '../forms/GitConnection';
import PersonalizedDocs from '../forms/PersonalizedDocs';
import PersonalizedDocsSetup from '../forms/PersonalizedDocs/Setup';
import ProjectSettingsForm from '../forms/Project';
import AllPages from '../forms/Project/AllPages';
import ApiReference from '../forms/Project/ApiReference';
import Changelog from '../forms/Project/Changelog';
import CustomCode from '../forms/Project/CustomCode';
import CustomLogin from '../forms/Project/CustomLogin';
import ErrorPages from '../forms/Project/ErrorPages';
import Glossary from '../forms/Project/Glossary';
import HealthCheck from '../forms/Project/HealthCheck';
import Integrations from '../forms/Project/Integrations';
import Navigation from '../forms/Project/Navigation';
import ProjectSettings from '../forms/Project/ProjectSettings';
import ThemeEditor from '../forms/Project/ThemeEditor';
import ReusableContent from '../forms/ReusableContent';

import styles from './index.module.scss';

function Configure() {
  const bem = useClassy(styles, 'Configure');
  const [searchQuery, setSearchQuery] = useState('');

  const planFeatures = useProjectStore(s => s.plan.features);

  const { project } = useContext(ProjectContext) as ProjectContextValue;
  const { domainFull: dashUrl } = useContext(ConfigContext) as ConfigContextValue;

  const isEnterpriseChild = useSuperHubStore(s => s.enterprise?.isChild);
  const isChildManagedBidi = isEnterpriseChild && project?.parent?.flags?.childManagedBidi;

  // links for nav
  const dashLinkUrl = useDashLinkUrl();
  const baseUrl = useContext(BaseUrlContext);
  const cleanBaseUrl = baseUrl === '/' ? '' : baseUrl;
  const {
    browserRouterLocation: { pathname },
  } = useSuperHubContext();

  const logoutUrl = `/logout?redirect_uri=${cleanBaseUrl}${pathname}`;

  // god mode
  const { isGod } = useContext(UserContext) as unknown as UserContextValue;
  const godmodeUrl = isGod ? dashLinkUrl('settings') : undefined;

  // personalized docs
  const isSetupComplete = useIsWebhookSetupComplete();

  // graphql
  const hasGraphQL = project?.flags.graphql;

  // links for enterprise
  const isEnterprise = useSuperHubStore(s => !!s.enterprise);
  const isSingleProjectEnterpriseChild = useSuperHubStore(s => s.enterprise?.isChild && s.enterprise?.isSingleProject);
  const [parentSubdomain] = useProjectStore(s => [s.data.parent]);
  const enterpriseGroupUrl = isEnterprise ? dashLinkUrl('settings', { toEnterpriseGroup: true }) : undefined;
  const showGroupLink = !!parentSubdomain && !!enterpriseGroupUrl;

  const configureNavData = useMemo(
    () =>
      [
        {
          section: 'Project',
          items: [
            {
              icon: 'toggle-right',
              keywords: ['project', 'settings', 'robots.txt', 'sitemap', 'access'],
              label: 'General',
              to: '/configure/project-settings',
            },
            ...(isEnterpriseChild
              ? []
              : [
                  {
                    icon: 'unlock',
                    keywords: ['project', 'jwt', 'logout', 'login', 'user data expiration'],
                    label: 'Custom Login',
                    to: '/configure/custom-login',
                  },
                ]),
            {
              icon: 'pie-chart',
              keywords: ['project', 'analytics', 'page views'],
              label: 'Documentation Metrics',
              href: dashLinkUrl('metrics/page-views'),
              target: '_blank',
            },
            {
              icon: 'alert-triangle',
              keywords: ['project', '404', 'redirect'],
              label: 'Error Pages',
              to: '/configure/error-pages',
              planFeature: 'errors',
            },
            {
              icon: 'bookmark',
              keywords: ['project', 'terms'],
              label: 'Glossary',
              to: '/configure/glossary',
              planFeature: 'glossary',
            },
            ...(hasGraphQL
              ? [
                  {
                    icon: 'graphql',
                    keywords: ['project'],
                    label: 'GraphQL',
                    href: dashLinkUrl('graphql'),
                    target: '_blank',
                  } satisfies SidebarNavLinkProps,
                ]
              : []),
            ...(!isEnterpriseChild || isChildManagedBidi
              ? [
                  {
                    icon: 'git-branch',
                    isBeta: true,
                    keywords: ['project', 'sync', 'bidi', 'two-way'],
                    label: 'Git Connection',
                    to: '/configure/git-connection',
                  },
                ]
              : []),
            {
              icon: 'radio',
              keywords: ['project', 'statuspage'],
              label: 'Health Check',
              to: '/configure/health-check',
              planFeature: 'healthCheck',
            },
            {
              icon: 'minimize-2',
              keywords: [
                'project',
                'google',
                'bing',
                'intercom',
                'zendesk',
                'segment',
                'localize',
                'heap',
                'koala',
                'typekit',
                'recaptcha',
              ],
              label: 'Integrations',
              to: '/configure/integrations',
            },
            {
              icon: 'home',
              keywords: ['project', 'home'],
              label: 'Landing Page',
              href: isSingleProjectEnterpriseChild
                ? dashLinkUrl('global-landing-page', { toEnterpriseGroup: true })
                : dashLinkUrl('landing'),
              target: '_blank',
              planFeature: 'landing_page',
            },
            {
              icon: 'key',
              keywords: ['project', 'variables'],
              label: 'Personalized Docs',
              to: isSetupComplete ? '/configure/personalized-docs' : '/configure/personalized-docs/setup',
            },
          ],
        },
        {
          section: 'Versioned Content',
          items: [
            {
              icon: 'component',
              keywords: ['versioned content', 'mdx', 'jsx'],
              label: 'Custom Components',
              to: '/configure/custom-components',
            },
            {
              icon: 'recycle',
              keywords: ['versioned content', 'mdx', 'jsx'],
              label: 'Reusable Content',
              to: '/configure/reusable-content',
              planFeature: 'reusableContent',
            },
          ],
        },
        {
          section: 'Appearance',
          items: [
            {
              icon: 'code',
              keywords: [
                'appearance',
                'expand response',
                'response examples',
                'default values',
                'sdk',
                'request history',
              ],
              label: 'API Reference',
              to: '/appearance/api-reference',
              planFeature: 'usability',
            },
            {
              icon: 'megaphone',
              keywords: ['appearance', 'show author', 'date display', 'continuous', 'collapsed'],
              label: 'Changelog',
              to: '/appearance/changelog',
              planFeature: 'usability',
            },
            {
              icon: 'component',
              keywords: ['appearance', 'code', 'head', 'body'],
              label: 'CSS, JS, HTML',
              to: '/appearance/custom-code',
              planFeature: 'stylesheet',
            },
            {
              icon: 'layout',
              keywords: ['appearance', 'code', 'readme logo', 'logo', 'navigation'],
              label: 'Header & Footer',
              to: '/appearance/navigation',
            },
            {
              icon: 'columns-3-cog',
              keywords: ['appearance', 'table of contents', 'toc', 'whats next'],
              label: 'Page Settings',
              to: '/appearance/all-pages',
            },
            {
              icon: 'brush',
              keywords: ['appearance', 'logo', 'color', 'favicon'],
              label: 'Theme',
              to: '/appearance/theme-editor',
            },
          ],
        },
        {
          section: 'Admin',
          items: [
            {
              icon: 'key',
              keywords: ['admin', 'access'],
              label: 'API Keys',
              href: dashLinkUrl('api-key'),
              target: '_blank',
            },
            ...(isEnterpriseChild
              ? [
                  {
                    icon: 'log-in',
                    keywords: ['admin', 'end user', 'login', 'safelist', 'logout', 'expiration'],
                    label: 'End User Login',
                    href: dashLinkUrl('end-users/login', { toEnterpriseGroup: true }),
                    target: '_blank',
                  } satisfies SidebarNavLinkProps,
                ]
              : []),
            {
              icon: 'globe',
              keywords: ['admin'],
              label: 'Custom Domain',
              href: dashLinkUrl('domains', { toEnterpriseGroup: isEnterpriseChild }),
              target: '_blank',
              planFeature: 'custom_domain',
            },
            {
              icon: 'labs',
              keywords: ['admin'],
              label: 'Labs',
              href: dashLinkUrl('labs'),
              target: '_blank',
            },
            {
              icon: 'credit-card',
              keywords: ['admin', 'billing'],
              label: 'Manage Plan',
              href: dashLinkUrl('plans'),
              target: '_blank',
            },
            {
              icon: 'teammates',
              keywords: ['admin', 'access'],
              label: 'Manage Team',
              href: isEnterpriseChild ? dashLinkUrl('team', { toEnterpriseGroup: true }) : dashLinkUrl('users'),
              target: '_blank',
            },
          ],
        },
      ] satisfies { items: SidebarNavLinkProps[]; section: string }[],
    [dashLinkUrl, hasGraphQL, isChildManagedBidi, isEnterpriseChild, isSetupComplete, isSingleProjectEnterpriseChild],
  );

  const filteredNavData = useMemo(() => {
    if (!searchQuery.trim()) return configureNavData;

    const query = searchQuery.toLowerCase();
    return configureNavData
      .map(section => ({
        ...section,
        items: section.items.filter(
          (item: SidebarNavLinkProps) =>
            item.label.toLowerCase().includes(query) ||
            item.keywords?.some(keyword => keyword.toLowerCase().includes(query)),
        ),
      }))
      .filter(section => section.items.length > 0);
  }, [configureNavData, searchQuery]);

  return (
    <BasePanel className={bem('')} label="Project settings controls" safeStyles={false}>
      <div className={bem('-layout')} data-testid="configure-panel">
        <SidebarNav className={bem('-sidebar', !!showGroupLink && bem('-sidebar_enterprise'))}>
          <Flex align="stretch" className={bem('-sidebar-container')} gap="sm" justify="start" layout="col">
            <Flex align="center" aria-label="Search" className={bem('-search')} gap="0" justify="start" tag="label">
              <Icon aria-hidden="true" className={bem('-search-icon')} name="search" />
              <input
                autoFocus
                className={bem('-search-input')}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search"
                type="search"
                value={searchQuery}
              />
              {!!searchQuery && (
                <button className={bem('-search-clear')} onClick={() => setSearchQuery('')} type="button">
                  <Icon aria-label="Clear search" name="x" />
                </button>
              )}
            </Flex>
            {filteredNavData.map(({ section, items }) => (
              <SidebarNavSection key={section} label={section}>
                {items.map((props: SidebarNavLinkProps) => (
                  <SidebarNavLink key={`configure-${section}-${props.label}`} {...props} />
                ))}
              </SidebarNavSection>
            ))}
            {filteredNavData.length === 0 && <div className={bem('-search-empty')}>No results</div>}
          </Flex>
          {!!showGroupLink && (
            <Flex className={bem('-enterprise')} gap="0" justify="center">
              <div className={bem('-enterprise-grid')} />
              <div className={bem('-enterprise-corners')} />
              <a className={bem('-enterprise-link')} href={enterpriseGroupUrl} rel="noreferrer" target="_blank">
                <Truncate>{parentSubdomain}</Truncate>
                <Icon
                  aria-label="Opens in a new tab"
                  className={bem('-enterprise-link-newTab')}
                  name="arrow-up-right"
                />
              </a>
            </Flex>
          )}
          <Flex align="center" className={bem('-footer')} gap="0">
            <Dropdown appendTo={() => document.body}>
              <button className={bem('-footer-link')} type="button">
                <Icon className={bem('-footer-link-icon')} name="circle-user-round" />
              </button>
              <Menu theme="dark">
                <MenuItem
                  href={`${dashUrl}/user/edit`}
                  icon={<Icon aria-hidden="true" name="user" />}
                  TagName="a"
                  target="_blank"
                >
                  <span>Account</span>
                </MenuItem>
                <MenuDivider />
                <MenuItem href={logoutUrl} icon={<Icon aria-hidden="true" name="log-out" />} TagName="a">
                  Log Out
                </MenuItem>
              </Menu>
            </Dropdown>
            <Flex gap="xs">
              {!!isGod && (
                <a
                  className={bem('-footer-link', '-footer-link_godmode')}
                  href={godmodeUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  God
                  <Icon aria-label="Opens in a new tab" className={bem('-footer-link-newTab')} name="arrow-up-right" />
                </a>
              )}
              <a className={bem('-footer-link')} href={dashUrl} rel="noopener noreferrer" target="_blank">
                All Projects{' '}
                <Icon aria-label="Opens in a new tab" className={bem('-footer-link-newTab')} name="arrow-up-right" />
              </a>
            </Flex>
          </Flex>
        </SidebarNav>

        {/* Routes to render sub-forms, grouped by sections in alphabetical order for readability */}
        <Switch>
          {/* Project */}
          <Route exact path="/:action(configure)">
            <Redirect to="/configure/project-settings" />
          </Route>
          <Route path="/:action(configure)/:section(project-settings)">
            <ProjectSettingsForm>
              <ProjectSettings />
            </ProjectSettingsForm>
          </Route>
          <Route path="/:action(configure)/:section(custom-login)">
            <ProjectSettingsForm>
              <CustomLogin />
            </ProjectSettingsForm>
          </Route>
          <Route path="/:action(configure)/:section(error-pages)">
            <ProjectSettingsForm formGate={planFeatures.errors ? 'none' : 'plan'}>
              <ErrorPages />
            </ProjectSettingsForm>
          </Route>
          <Route path="/configure/git-connection">
            <GitConnection />
          </Route>
          <Route path="/:action(configure)/:section(glossary)">
            <ProjectSettingsForm>
              <Glossary />
            </ProjectSettingsForm>
          </Route>
          <Route path="/:action(configure)/:section(health-check)">
            <ProjectSettingsForm formGate={planFeatures.healthCheck ? 'none' : 'plan'}>
              <HealthCheck />
            </ProjectSettingsForm>
          </Route>
          <Route path="/:action(configure)/:section(integrations)">
            <ProjectSettingsForm>
              <Integrations />
            </ProjectSettingsForm>
          </Route>
          <Route path="/:action(configure)/:section(personalized-docs)/:type(setup)">
            <ProjectSettingsForm>
              <PersonalizedDocsSetup />
            </ProjectSettingsForm>
          </Route>
          <Route path="/:action(configure)/:section(personalized-docs)">
            <ProjectSettingsForm>
              <PersonalizedDocs />
            </ProjectSettingsForm>
          </Route>

          {/* Versioned Content */}
          <Route path="/:action(configure)/:section(reusable-content)">
            <ReusableContent />
          </Route>
          <Route path="/:action(configure)/:section(custom-components)">
            <CustomComponents />
          </Route>

          {/* Appearance */}
          <Route path="/:action(appearance)/:section(api-reference)">
            <ProjectSettingsForm>
              <ApiReference />
            </ProjectSettingsForm>
          </Route>
          <Route path="/:action(appearance)/:section(changelog)">
            <ProjectSettingsForm formGate={planFeatures.changelogs ? 'none' : 'demo'}>
              <Changelog />
            </ProjectSettingsForm>
          </Route>
          <Route path="/:action(appearance)/:section(custom-code)">
            <ProjectSettingsForm formGate={planFeatures.stylesheet ? 'none' : 'demo'}>
              <CustomCode />
            </ProjectSettingsForm>
          </Route>
          <Route path="/:action(appearance)/:section(navigation)">
            <ProjectSettingsForm formGate={planFeatures.usability ? 'none' : 'demo'}>
              <Navigation />
            </ProjectSettingsForm>
          </Route>
          <Route path="/:action(appearance)/:section(all-pages)">
            <ProjectSettingsForm>
              <AllPages />
            </ProjectSettingsForm>
          </Route>
          <Route path="/:action(appearance)/:section(theme-editor)">
            <ProjectSettingsForm>
              <ThemeEditor />
            </ProjectSettingsForm>
          </Route>
        </Switch>
      </div>
    </BasePanel>
  );
}

export default Configure;
